<template>
    <div>
        <sau-template :retrieved="gotData">
            <!-- Fetching Placeholder -->
            <template slot="message">Fetching Current Status</template>

            <!-- Data -->
            <template slot="data">
                <template v-if="processing">
                    <div class="row">
                        <div class="col">
                            <div class="sau-bx bx-pad text-center">
                                <h3>{{ actions[action.toLowerCase()] }}</h3>
                                <img src="/img/spinner-dark-gray-70.gif" alt="spinner" class="m-6"/>
                                <p>Please wait for the current action to finish before interacting with your server</p>
                                <p>Navigating away from this page will not disrupt the current action, however, reloading the page may display a temporary status while the action completes.</p>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-else-if="!support">
                    <!-- show available power options -->
                    <div class="row">
                        <div class="col">
                            <h3>Power Controls</h3>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-lg-6 pb-6">
                            <button
                                class="sau-power lg"
                                :class="powerCss('on')"
                                @click.prevent="performAction('start')"
                                :disabled="deviceOn"
                            >
                                <i class="far fa-power-off pt-2" aria-hidden="true"></i>
                                <p class="pt-2">
                                    Start
                                    <span
                                        v-if="deviceOn"
                                        class="sau-help sm ml-2 align-middle"
                                        v-b-tooltip.hover
                                        :title="on.start"
                                    >?</span>
                                    <span
                                        v-else
                                        class="sau-help sm ml-2 align-middle"
                                        v-b-tooltip.hover
                                        :title="off.start"
                                    >?</span>
                                </p>
                            </button>
                        </div>

                        <div class="col-lg-6 pb-6">
                            <button
                                class="sau-power lg"
                                :class="powerCss('off')"
                                @click.prevent="performAction('stop')"
                                :disabled="!deviceOn"
                            >
                                <i class="far fa-power-off pt-2" aria-hidden="true"></i>
                                <p class="pt-2">
                                    Stop
                                    <span
                                        v-if="!deviceOn"
                                        class="sau-help sm ml-2 align-middle"
                                        v-b-tooltip.hover
                                        :title="off.stop"
                                    >?</span>
                                    <span
                                        v-else
                                        class="sau-help sm ml-2 align-middle"
                                        v-b-tooltip.hover
                                        :title="on.stop"
                                    >?</span>
                                </p>
                            </button>
                        </div>
                    </div>

                    <div class="row pt-4">
                        <div class="col-lg-4 drop">
                            <button
                                @click.prevent="performAction('reboot')"
                                class="sau-power sm"
                                :class="action === 'reboot' ? 'yellow':'muted'"
                                :disabled="!availableActions('reboot')"
                            >
                                <i class="far fa-redo" aria-hidden="true"></i>
                                <p class="pt-2">
                                    Reboot
                                    <span
                                        class="sau-help sm ml-2 align-middle"
                                        v-b-tooltip.hover
                                        :title="messages.reboot"
                                    >?</span>
                                </p>
                            </button>
                        </div>
                        <div class="col-lg-4">
                            <button
                                @click.prevent="performAction('shutdown')"
                                class="sau-power sm"
                                :class="action === 'shutdown' ? 'yellow':'muted'"
                                :disabled="!availableActions('shutdown')"
                            >
                                <i class="far fa-plug" aria-hidden="true"></i>
                                <p class="pt-2">
                                    Shutdown
                                    <span
                                        class="sau-help sm ml-2 align-middle"
                                        v-b-tooltip.hover
                                        :title="messages.shutdown"
                                    >?</span>
                                </p>
                            </button>
                        </div>
                        <div class="col-lg-4">
                            <button
                                v-b-modal.modal-rebuild-vps
                                class="sau-power sm"
                                :class="action === 'rebuild' ? 'yellow':'muted'"
                                :disabled="!availableActions('rebuild')"
                            >
                                <i class="far fa-redo" aria-hidden="true"></i>
                                <p class="pt-2">
                                    Rebuild
                                    <span
                                        class="sau-help sm ml-2 align-middle"
                                        v-b-tooltip.hover
                                        :title="messages.rebuild"
                                    >?</span>
                                </p>
                            </button>
                        </div>

                        <!-- Rebuild Modal -->
                        <b-modal
                            id="modal-rebuild-vps"
                            centered
                            title="Rebuild VPS"
                            @ok="rebuild"
                            @cancel="template = ''"
                            ok-variant="success"
                            :ok-disabled="!template"
                            ok-title="Rebuild VPS"
                            ref="rebuildVPS"
                        >
                            <div class="row">
                                <div class="col">
                                    <p
                                        v-if="device.virtual_machine"
                                    >Current Template: {{ device.virtual_machine.template_label }}</p>
                                    <label for="template">New Template</label>
                                    <b-form-select
                                        id="template"
                                        v-model="template"
                                        :options="templates"
                                        size="sm"
                                    />
                                </div>
                            </div>
                        </b-modal>
                    </div>
                </template>

                <template v-else>
                    <!-- show support form -->
                    <div class="row">
                        <div class="col">
                            <div class="sau-bx bx-pad">
                                <h3>We are having issues accessing your Server!</h3>
                                <p>No response has been received since the requested action was performed. This is usually a temporary issue.</p>
                                <p>If you would like assistance from one of our support staff please click the link below.</p>
                                <sau-utility-support></sau-utility-support>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
        </sau-template>
    </div>
</template>

<script>
import { setInterval, clearInterval, clearTimeout, setTimeout } from 'timers';
export default {
    props: ['service'],
    data() {
        return {
            device: {},
            deviceOn: '',
            id: '',
            support: false,
            processing: false,
            poll: '',
            action: '',
            templates: [],
            template: ''
        };
    },
    created() {
        this.startApp();
    },
    methods: {
        startApp() {
            this.getDeviceStatus().then(response => {
                if (response.data) {
                    this.deviceOn = response.data.virtual_machine.booted;
                    this.device = response.data;
                    this.formatTemplates(response.data.templates);
                    this.support = false;
                }
            });
        },
        formatTemplates(templates) {
            _.forIn(
                templates,
                function(key, val) {
                    this.templates.push({ value: val, text: key });
                }.bind(this)
            );
        },
        checkServerResult() {
            /** Called after performing an action on the server
             * - compare against last action
             * So if action = power off, wait till we get a 'off' powerstatus response (not on) etc
             */

            this.getDeviceStatus()
                .then(response => {
                    if (response.data) {
                        // Compare the response against the action we requested
                        this.deviceOn = response.data.virtual_machine.booted;
                        // Return if no action is set
                        if (!this.action) return;
                        switch (this.action.toLowerCase()) {
                            case 'start':
                            case 'reboot':
                                // Need an 'on' response'
                                if (
                                    response.data.virtual_machine.booted &&
                                    !response.data.virtual_machine.locked
                                ) {
                                    this.cancelPolling();
                                }
                                break;
                            case 'stop':
                            case 'shutdown':
                                // Need a 'off' response
                                if (
                                    !response.data.virtual_machine.booted &&
                                    !response.data.virtual_machine.locked
                                ) {
                                    this.cancelPolling();
                                }
                                break;
                            case 'rebuild':
                                // Need a built:true response
                                if (
                                    response.data.virtual_machine.built &&
                                    !response.data.virtual_machine.locked
                                ) {
                                    this.cancelPolling();
                                }
                                break;
                            default:
                                console.log(
                                    'Unknown remote management action: ' +
                                        this.action
                                );
                                break;
                        }
                    }
                })
                .catch(error => {
                    this.support = true;
                });
        },
        powerCss(state) {
            switch (state) {
                case 'on':
                    return this.action
                        ? 'muted'
                        : this.deviceOn
                        ? 'green'
                        : 'muted';
                    break;
                case 'off':
                    return this.action
                        ? 'muted'
                        : this.deviceOn
                        ? 'muted'
                        : 'red';
                    break;
            }
        },
        cancelPolling() {
            if (this.poll) {
                clearInterval(this.poll);
                this.poll = null;
                this.processing = false;
                this.action = '';
            }
        },
        rebuild() {
            let message = '';
            let updating = _.find(
                this.templates,
                function(template) {
                    return template.value == this.template;
                }.bind(this)
            );
            if (this.device.virtual_machine) {
                message =
                    'You are about to update the current template from ' +
                    this.device.virtual_machine.template_label +
                    ' to ' +
                    updating.text +
                    '<br /> Rebuild will detroy all data on the Server. Are you sure you want to rebuild this server?';
            } else {
                message =
                    'You are about to update the current template to ' +
                    updating.text +
                    '<br /> Rebuild will detroy all data on the Server. Are you sure you want to rebuild this server?';
            }
            this.$swal.queue([
                {
                    title: 'Are you sure?',
                    html: message,
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, rebuild',
                    confirmButtonClass: 'btn btn-success',
                    cancelButtonText: 'No, cancel',
                    cancelButtonClass: 'btn btn-link',
                    buttonsStyling: false,
                    reverseButtons: true,
                    preConfirm: () => {
                        return this.rebuildVpsTemplate({
                            template_id: this.template
                        })
                            .then(response => {
                                if (response.data) {
                                    this.processing = true;
                                    this.action = 'rebuild';
                                    // Set timeout to poll server power to see when the requested action has completed
                                    this.poll = setInterval(
                                        function() {
                                            this.checkServerResult();
                                        }.bind(this),
                                        3000
                                    );
                                } else {
                                    this.$swal.insertQueueStep({
                                        type: 'error',
                                        title: 'There seems to be a problem',
                                        html:
                                            'There was an issue trying to update your template. Please try again shortly.'
                                    });
                                    this.processing = false;
                                }
                            })
                            .catch(error => {
                                this.$swal.insertQueueStep({
                                    type: 'error',
                                    title:
                                        'There was an issue trying to update your template',
                                    html:
                                        '<p>This could be due to a temporary locked state if any action was just performed.</p><p>Please try again shortly.</p>'
                                });
                                this.processing = false;
                            });
                    }
                }
            ]);
        },
        performAction(action) {
            let msg = '';
            let title = '';
            let actionConfirmButtonClass = 'btn btn-danger';
            switch (action) {
                case 'reboot':
                    msg = this.messages.reboot;
                    title = 'Yes, reboot it!';
                    break;
                case 'shutdown':
                    msg = this.messages.shutdown;
                    title = 'Yes, shut it down!';
                    break;
                case 'start':
                    msg = this.messages.start;
                    title = 'Yes, start the VPS!';
                    actionConfirmButtonClass = 'btn btn-success';
                    break;
                case 'stop':
                    msg = this.messages.stop;
                    title = 'Yes, stop the VPS!';
                    break;
            }
            this.$swal.queue([
                {
                    title: 'Are you sure?',
                    text: msg,
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonText: title,
                    confirmButtonClass: actionConfirmButtonClass,
                    cancelButtonText: 'No, cancel',
                    cancelButtonClass: 'btn btn-link',
                    buttonsStyling: false,
                    reverseButtons: true,
                    preConfirm: () => {
                        return axios
                            .post(
                                '/api/services/' +
                                    this.getServiceType() +
                                    '/' +
                                    this.getServiceId() +
                                    '/' +
                                    action,
                                {}
                            )
                            .then(response => {
                                if (response.data) {
                                    this.processing = true;
                                    this.action = action;
                                    // Set timeout to poll server power to see when the requested action has completed
                                    this.poll = setInterval(
                                        function() {
                                            this.checkServerResult();
                                        }.bind(this),
                                        3000
                                    );
                                } else {
                                    this.$swal.insertQueueStep({
                                        type: 'error',
                                        title: 'There seems to be a problem',
                                        html:
                                            'There was an issue trying to perform the requested action. Please try again shortly.'
                                    });
                                    this.processing = false;
                                    this.support = true;
                                }
                            })
                            .catch(error => {
                                this.$swal.insertQueueStep({
                                    type: 'error',
                                    title: 'There seems to be a problem',
                                    html:
                                        'There was an issue trying to perform the requested action. Please try again shortly.'
                                });
                                this.processing = false;
                                this.support = true;
                            });
                    }
                }
            ]);
        },
        availableActions(action) {
            switch (action) {
                case 'start':
                case 'rebuild':
                    return this.deviceOn ? false : true;
                    break;
                case 'reboot':
                case 'shutdown':
                case 'stop':
                    return this.deviceOn ? true : false;
                    break;
            }
        }
    }
};
</script>

<style>
.myactive {
    background-color: red;
}
button:disabled {
    cursor: not-allowed;
}
.muted:hover {
    color: #e5e5e5 !important;
    border: 1px solid #e5e5e5 !important;
}
.muted:hover i {
    color: #e5e5e5 !important;
}
.muted:not([disabled]) {
    color: #666666 !important;
    border: 1px solid #666666 !important;
}
.muted:not([disabled]) i {
    color: #666666 !important;
}
</style>
